import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {HandleType, NodeProps} from 'reactflow';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ICON_SIZE} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import SimpleBenchlingNode from 'ui-library/reactflow/nodes/SimpleBenchlingNode/SimpleBenchlingNode';
import {SimpleBenchlingNodeData} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/SimpleBenchlingNodeTypes';

import {InvisibleNodeHandle} from '../../../../ui-library/reactflow/handles/InvisibleNodeHandle';

export enum NodeType {
  SOURCE_PMEI = 'SOURCE_PMEI',
  DESTINATION_PMEI = 'DESTINATION_PMEI',
}

interface UnitOperationFlowchartNodeData {
  pmeiId: string;
  procedureRunName: string | null;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
  status: ProcessTaskStatusType | null;
}

interface GetSimpleBenchlingNodeDataArgs {
  nodeType: string;
  methodName: string;
  procedureRunName: string | null;
  conditionName: string;
  replicateNumber: number;
  status: ProcessTaskStatusType | null;
}

/* Exported for testing */
export const UNIT_OPERATION_NODE_HEADER_COLORS = {
  [ProcessTaskStatusType.COMPLETED]: Color.GREEN_100,
  [ProcessTaskStatusType.FAILED]: Color.GRAY_100,
  [ProcessTaskStatusType.IN_PROGRESS]: Color.BLUE_100,
  [ProcessTaskStatusType.SKIPPED]: Color.GRAY_100,
  [ProcessTaskStatusType.CANCELLED]: Color.GRAY_100,
  [ProcessTaskStatusType.PENDING]: Color.WHITE,
  [ProcessTaskStatusType.INVALID]: Color.GRAY_100,
};

function getHeaderProps(status: ProcessTaskStatusType | null): {
  headerIcon: JSX.Element;
  headerColor: string | undefined;
} {
  const headerIcon = status ? (
    <TaskStatusIcon type={status} />
  ) : (
    <BenchlingIcon icon="calendar-days" className={css(styles.plannedIcon)} />
  );
  const headerColor = status ? UNIT_OPERATION_NODE_HEADER_COLORS[status] : undefined;
  return {
    headerIcon,
    headerColor,
  };
}

function getSimpleBenchlingNodeData({
  nodeType,
  methodName,
  procedureRunName,
  conditionName,
  replicateNumber,
  status,
}: GetSimpleBenchlingNodeDataArgs): SimpleBenchlingNodeData {
  return {
    nodeHandleCustomizations: {
      nodeHeaderHandleRenderer: ({nodeId, handleType}) => (
        <InvisibleNodeHandle
          nodeId={nodeId}
          handleType={handleType}
          nodeHeaderHandleIdGenerator={(nodeId: string, handleType: HandleType) =>
            // All nodes have source handles, but only dest nodes have target handles.
            nodeType === NodeType.DESTINATION_PMEI || handleType === 'source'
              ? `${nodeId}-${handleType}`
              : null
          }
        />
      ),
    },
    hideSectionSummaries: true,
    nodeName: methodName,
    nodeSecondaryInfo: procedureRunName ?? '',
    sections: [
      {
        sectionName: `${conditionName} - ${replicateNumber}`,
        sectionRows: [],
      },
    ],
    ...getHeaderProps(status),
  };
}

export default function UnitOperationFlowchartNode({
  data,
  ...props
}: NodeProps<UnitOperationFlowchartNodeData>): JSX.Element {
  const {methodName, procedureRunName, conditionName, replicateNumber, status} = data;

  const simpleBenchlingNodeData = getSimpleBenchlingNodeData({
    nodeType: props.type,
    methodName,
    procedureRunName,
    conditionName,
    replicateNumber,
    status,
  });

  return (
    <div className={css(styles.node)}>
      <SimpleBenchlingNode {...props} data={simpleBenchlingNodeData} />
    </div>
  );
}

const styles = StyleSheet.create({
  node: {
    ':hover': {
      cursor: 'default',
    },
  },
  plannedIcon: {
    marginRight: 6,
    width: ICON_SIZE.MEDIUM,
    height: ICON_SIZE.MEDIUM,
  },
});
